import is from '@sindresorhus/is';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { ApplicationContext } from '../../../../shared/source/context/context';
import {
    searchTypeLiteralSchema,
    stateTypeLiteralSchema,
} from '../../../../shared/source/data-contract/filter-response';
import {
    createCustomFilterSchema,
    createPreDefinedFilterSchemas,
    createSortFilterStateByOrder,
    creatorFilterStateSchema,
    creatorLiteralSchema,
    customFilterKeySchema,
    customFilterStateSchema,
    durationFilterStateSchema,
    durationLiteralSchema,
    getFilterValuesFromFilterValueString,
    hiringManagerFilterStateSchema,
    hiringManagerLiteralSchema,
    ownerFilterStateSchema,
    ownerLiteralSchema,
    publishedAtFilterStateSchema,
    publishedAtLiteralSchema,
    reduceFiltersToKeyValuePairs,
    seniorityLevelFilterStateSchema,
    seniorityLevelLiteralSchema,
    setFilterStateValuesToFilter,
    startDateFilterStateSchema,
    startDateLiteralSchema,
    updatedAtFilterStateSchema,
    updatedAtLiteralSchema,
    workPlaceFilterStateSchema,
    workPlaceLiteralSchema,
} from '../../../../shared/source/data-contract/filter-state';
import { filterResponseTransformedSchema } from '../../data-contract/filter-response-transformed';
import { convertFilterStateToFilters } from '../common/filter-sidebar/utils';

import type { GeneralFilter } from '../../../../shared/source/data-contract/filter';
import type {
    FilterState,
    FilterWithFilterStates,
    OwnerFilterState,
} from '../../../../shared/source/data-contract/filter-state';
import type { JobState } from '../../../../shared/source/data-contract/job';
import type { Pathname } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { FilterInFilterResponseTransformed } from '../../data-contract/filter-response-transformed';
import type { UseQueryResult } from '@tanstack/react-query';

const allowedJobFilterInOrder = [
    ownerFilterStateSchema,
    hiringManagerFilterStateSchema,
    creatorFilterStateSchema,
    startDateFilterStateSchema,
    publishedAtFilterStateSchema,
    updatedAtFilterStateSchema,
    workPlaceFilterStateSchema,
    seniorityLevelFilterStateSchema,
    durationFilterStateSchema,
] as const;

const filterOrder = allowedJobFilterInOrder.map(({ shape }) => shape.field.value);
const allowedJobFilterFieldNameSchema = z.union([
    ownerLiteralSchema,
    hiringManagerLiteralSchema,
    creatorLiteralSchema,
    startDateLiteralSchema,
    publishedAtLiteralSchema,
    updatedAtLiteralSchema,
    workPlaceLiteralSchema,
    seniorityLevelLiteralSchema,
    durationLiteralSchema,
]);
const allowedJobAndCustomFilterFieldNameSchema = allowedJobFilterFieldNameSchema.or(customFilterKeySchema);
const allowedJobFilterSchema = z.union(allowedJobFilterInOrder);
const allowedJobAndCustomFilterSchema = allowedJobFilterSchema.or(customFilterStateSchema);

type AllowedJobFilter = z.infer<typeof allowedJobFilterSchema>;

type AllowedJobOrCustomFilter = z.infer<typeof allowedJobAndCustomFilterSchema>;

const sortFilters = createSortFilterStateByOrder({ filterOrder });

const isAllowedOrCustomFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedJobOrCustomFilter =>
    allowedJobAndCustomFilterFieldNameSchema.safeParse(filter.field).success;

const isAllowedFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedJobFilter =>
    allowedJobFilterFieldNameSchema.safeParse(filter.field).success;

const filterQueryParam = 'jobFilters';

const isOwnerFilterState = (filterState: FilterState): filterState is OwnerFilterState =>
    ownerFilterStateSchema.safeParse(filterState).success;

export type UseJobFilterResult = {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly filters: readonly FilterWithFilterStates[];
    readonly hasActiveFilters: boolean;
    readonly resetFilterValues: () => void;
    readonly saveFilterValues: (updatedFilters: readonly FilterWithFilterStates[]) => void;
};

export const useJobFilter = (filterState?: JobState | 'all'): UseQueryResult<UseJobFilterResult> => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language, locationSearchParams } = applicationState;
    const myUserId = applicationState.myUserProfile.get('id').unwrapOr('');
    const pathname: Pathname = `/jobs/filter`;

    const resetFilterValues = (): void => {
        applicationState.locationSearchParams.delete(filterQueryParam);
        actions.setLocationSearchParams(applicationState.locationSearchParams);
    };

    const saveFilterValues = (updatedFilters: readonly FilterWithFilterStates[]): void => {
        const filterKeyValuePairs = reduceFiltersToKeyValuePairs(updatedFilters);

        if (is.emptyObject(filterKeyValuePairs)) {
            resetFilterValues();
        } else {
            applicationState.locationSearchParams.set(filterQueryParam, JSON.stringify(filterKeyValuePairs));
            actions.setLocationSearchParams(applicationState.locationSearchParams);
        }
    };

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(filterResponseTransformedSchema, {
                pathname,
            });

            if (result.isOk) {
                const allowedJobFilterSchemas = createPreDefinedFilterSchemas({ translate });
                const customJobFilterSchema = createCustomFilterSchema({ attributeNamespace: 'jobs', translate });

                const filterStates = result.value
                    .filter(isAllowedOrCustomFilter)
                    .sort(sortFilters)
                    .map((filter) => {
                        if (isAllowedFilter(filter)) {
                            const filterResult = allowedJobFilterSchemas[filter.field].safeParse(filter);

                            if (filterResult.success) {
                                return filterResult.data;
                            }
                        }

                        return customJobFilterSchema.parse(filter);
                    });

                return [
                    {
                        id: 'job',
                        filterStates: filterStates.map((filterState) => {
                            if (isOwnerFilterState(filterState) && is.nonEmptyString(myUserId)) {
                                return {
                                    ...filterState,
                                    options: [
                                        {
                                            id: myUserId,
                                            label: `- ${translate('pages.jobs.toolbar.owner-filter-label')} -`,
                                        },
                                        ...filterState.options,
                                    ],
                                };
                            }

                            return filterState;
                        }),
                        subTitle: translate('pages.jobs.filters.workspace-preferences.sub-title'),
                        title: translate('pages.jobs.filters.workspace-preferences.title'),
                    },
                ];
            }

            throw result.error;
        },
        queryKey: [pathname, 'get', language.code],
        select(filters) {
            const filterValuesJsonString = locationSearchParams.get(filterQueryParam);
            const search = locationSearchParams.get('searchTerm');
            const state = filterState ?? locationSearchParams.get('tab');
            const filterStatesWithSearchAndState: readonly FilterState[] = [
                {
                    field: 'title',
                    label: searchTypeLiteralSchema.value,
                    type: searchTypeLiteralSchema.value,
                    value: is.string(search) ? [search] : undefined,
                },
                {
                    field: stateTypeLiteralSchema.value,
                    label: stateTypeLiteralSchema.value,
                    type: stateTypeLiteralSchema.value,
                    value: is.string(state) && state !== 'all' ? [state] : undefined,
                },
            ];

            if (is.string(filterValuesJsonString)) {
                const filterValuesFromStorage = getFilterValuesFromFilterValueString({
                    filters,
                    filterValuesJsonString,
                });

                if (filterValuesFromStorage.isOk) {
                    const filtersWithValues = setFilterStateValuesToFilter(filters, filterValuesFromStorage.value);
                    const allFilterStatesWithValues = filtersWithValues.reduce(
                        (acc, { filterStates }) => [...acc, ...filterStates],
                        [],
                    );

                    return {
                        filterPayload: Maybe.of(
                            convertFilterStateToFilters([
                                ...allFilterStatesWithValues,
                                ...filterStatesWithSearchAndState,
                            ]),
                        ),
                        filters: filtersWithValues,
                        hasActiveFilters: allFilterStatesWithValues.some(({ value }) => !is.nullOrUndefined(value)),
                        resetFilterValues,
                        saveFilterValues,
                    };
                }
            }

            const allFilterStates = filters.reduce((acc, { filterStates }) => [...acc, ...filterStates], []);

            return {
                filterPayload: Maybe.of(
                    convertFilterStateToFilters([...allFilterStates, ...filterStatesWithSearchAndState]),
                ),
                filters,
                hasActiveFilters: allFilterStates.some(({ value }) => !is.nullOrUndefined(value)),
                resetFilterValues,
                saveFilterValues,
            };
        },
    });
};
